import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Typography,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

import { useNavigate, useParams } from "react-router-dom";
import { useDynamicApi } from "../../hooks/useApi";
import { API_ENDPOINTS, API_BASE_URL } from "../../config/api";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

const ContentDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to get token
  const getToken = () => {
    return (
      localStorage.getItem("adminToken") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("adminToken") ||
      sessionStorage.getItem("access_token")
    );
  };

  // Custom GET by ID function
  const customGetById = async (url) => {
    const token = getToken();
    if (!token) {
      Toast.error(t("common.error"));
      throw new Error("Authentication token not found");
    }

    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message || t("contentManager.failedFetchDetails"),
        );
      }

      return result;
    } catch (error) {
      console.error("GET by ID request failed:", error);
      throw error;
    }
  };

  // Load content details
  // const loadContentDetails = async () => {
  //   setLoading(true);
  //   try {
  //     const endpoint = `${API_ENDPOINTS.CONTENT_GET_BY_ID}/${id}`;
  //     const response = await customGetById(endpoint);

  //     if (response?.status === true && response.data) {
  //       setContent(response.data);
  //     } else {
  //       Toast.error(t("contentManager.failedFetchDetails"));
  //       navigate("/content");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching content details:", error);
  //     Toast.error(error.message || t("contentManager.failedFetchDetails"));
  //     navigate("/content");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const loadContentDetails = async () => {
    setLoading(true);
    try {
      const endpoint = `${API_ENDPOINTS.CONTENT_GET_BY_ID}/${id}`;
      const response = await getContentById(endpoint);

      if (response?.status === true && response.data) {
        setContent(response.data);
      } else {
        Toast.error(t("contentManager.failedFetchDetails"));
        navigate("/content");
      }
    } catch (error) {
      console.error("Error fetching content details:", error);
      Toast.error(error.message || t("contentManager.failedFetchDetails"));
      navigate("/content");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (id) {
      loadContentDetails();
    }
  }, [id]);
  const { execute: getContentById } = useDynamicApi("GET");
  // Format date
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Format page_type to readable title
  const formatPageType = (pageType) => {
    if (!pageType) return "N/A";

    // Convert snake_case to Title Case with spaces
    return pageType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (loading) return null;

  if (!content) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Title level={3}>{t("contentManager.noContentFound")}</Title>
          <Button type="primary" onClick={() => navigate("/content")}>
            {t("common.previous")} {t("contentManager.content")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="content-details-page">
      {/* Header with back button */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/content")}
          >
            {t("common.previous")} {t("contentManager.content")}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t("contentManager.contentDetails")}
          </Title>
        </Space>
        {/* <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/content/edit/${content.id}`)}
          style={{ background: "#52c41a", borderColor: "#52c41a" }}
        >
          {t("common.edit")}
        </Button> */}
      </div>

      {/* Main Content */}
      <Card>
        {/* Header Section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <div>
            <h1 style={{ margin: 0, marginBottom: "8px" }}>
              {formatPageType(content.page_type)}
            </h1>
            <Space size="middle" wrap>
              <Tag color="blue">{t("contentManager.contentPage")}</Tag>
              <Text type="secondary">
                <FileTextOutlined /> {t("contentManager.contentId")}:{" "}
                {content.id}
              </Text>
            </Space>
          </div>
        </div>

        {/* Content Details */}
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t("contentManager.pageType")} span={2}>
            {formatPageType(content.page_type)}
          </Descriptions.Item>

          <Descriptions.Item label={t("contentManager.content")} span={2}>
            <div
              style={{
                backgroundColor: "#f5f5f5",
                padding: "20px",
                borderRadius: "6px",
                maxHeight: "400px",
                overflowY: "auto",
                whiteSpace: "pre-wrap",
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    content.content ||
                    `<p><em>${t("contentManager.noContent")}</em></p>`,
                }}
              />
            </div>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.createdAt")} span={1}>
            <Space>
              <CalendarOutlined />
              {formatDateTime(content.created_at)}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.lastUpdated")} span={1}>
            <Space>
              <ClockCircleOutlined />
              {formatDateTime(content.updated_at || content.created_at)}
            </Space>
          </Descriptions.Item>

          {content.created_by && (
            <Descriptions.Item label={t("contentManager.createdBy")} span={2}>
              {content.created_by}
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Timeline Section */}
        <Divider orientation="left" style={{ margin: "24px 0 16px" }}>
          {t("contentManager.contentTimeline")}
        </Divider>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <CalendarOutlined style={{ color: "#52c41a", fontSize: "16px" }} />
            <div>
              <Text strong>{t("contentManager.created")}</Text>
              <br />
              <Text type="secondary">{formatDateTime(content.created_at)}</Text>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <ClockCircleOutlined
              style={{ color: "#1890ff", fontSize: "16px" }}
            />
            <div>
              <Text strong>{t("common.lastUpdated")}</Text>
              <br />
              <Text type="secondary">
                {formatDateTime(content.updated_at || content.created_at)}
              </Text>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ContentDetails;
